"use client"

import axios from "axios"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Modal, Dropdown, Row, Col, Form, InputGroup } from "react-bootstrap"

// Local subcomponents
import ItemManagement from "./ItemManagement"
import DocumentUpload from "./DocumentUpload"
import InvoiceGenerator from "./InvoiceGenerator"

// UI components (from design kit)
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Separator } from "../ui/separator"
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaUserTie, 
  FaCalendarAlt, 
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaShoppingBag,
  FaTag,
  FaCog,
  FaChevronDown,
  FaSearch,
  FaChevronLeft,
  FaTimes
} from "react-icons/fa"

const API_BASE = ""
const apiUrl = (path) => `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`

const normalizeItems = (items) =>
  (items || []).map((item) => ({
    id: item.id && String(item.id).indexOf(".") === -1 ? Number(item.id) : null,
    item_type: item.item_type || "Service",
    item_name: item.item_name?.trim() || "",
    description: item.description?.trim() || "",
    quantity: Number(item.quantity || 1),
    cost_price: Number(item.cost_price || 0),
    selling_price: Number(item.selling_price || 0),
    margin: Number(item.margin || 0),
    total_amount: Number(item.total_amount) || Number(item.quantity || 1) * Number(item.selling_price || 0),
  }))

export default function SalesForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  // Data Loading states
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [savedSalesData, setSavedSalesData] = useState(null)
  const [selectedSale, setSelectedSale] = useState(null)
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEdit)

  // Discard & Customer selection modal states
  const [showDiscardModal, setShowDiscardModal] = useState(false)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [customerSearchQuery, setCustomerSearchQuery] = useState("")

  // Save Confirmation Modal (Image 5 style)
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false)
  const [pendingSaveParams, setPendingSaveParams] = useState(null)

  // Form State matching screenshots 3 & 4 layout
  const [formData, setFormData] = useState({
    branch: "Main Branch",
    valueDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    salesperson: "",
    exchangeRate: 1.0,
    consolidatedInvoice: false,
    consolidatedStatement: false,
    notes: "",

    // Billing details
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerAddress: "",
    sameAsCustomer: false,
    billingName: "",
    billingAddress: "",
    billingStreet: "",
    billingCity: "",
    billingZip: "",
    billingPhone: "",
    billingTelephone: "",
    billingEmail: "",

    // Additional meta
    customerId: null,
    supplierId: 1,
    departureDate: "",
    returnDate: "",
    sellingPrice: 0,
    purchaseCost: 0,
    paidAmount: 0,
    discount: 0,
    tax: 0,
    destination: "Dubai",
    items: [],
    documents: [],
    currency: "USD",
    status: "draft",
  })

  // Receipts voucher modal state
  const [savedInvoiceId, setSavedInvoiceId] = useState(null)
  const currentInvoiceId = savedInvoiceId
  const [showReceiptModal, setShowReceiptModal] = useState(false)
  const [paymentMode, setPaymentMode] = useState("Cash")
  const [amountPaid, setAmountPaid] = useState("")
  const [invoiceSummary, setInvoiceSummary] = useState(null)

  const [showVoucherModal, setShowVoucherModal] = useState(false)
  const [voucherAmount, setVoucherAmount] = useState(0)
  const [voucherPayer, setVoucherPayer] = useState("")
  const [voucherPayee, setVoucherPayee] = useState("Rekonic")
  const [voucherDescription, setVoucherDescription] = useState("")
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 16))

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newValue = typeof value === "function" ? value(prev[field]) : value
      return { ...prev, [field]: newValue }
    })
  }

  // Auto-fill billing details if SAME AS CUSTOMER is checked (Image 4)
  useEffect(() => {
    if (formData.sameAsCustomer) {
      setFormData((prev) => ({
        ...prev,
        billingName: prev.customerName,
        billingEmail: prev.customerEmail,
        billingPhone: prev.customerPhone,
        billingAddress: prev.customerAddress,
        billingStreet: prev.billingStreet || "Street One",
        billingCity: prev.billingCity || prev.destination || "Dubai",
        billingZip: prev.billingZip || "00000",
        billingTelephone: prev.billingTelephone || prev.customerPhone || ""
      }))
    }
  }, [
    formData.sameAsCustomer, 
    formData.customerName, 
    formData.customerEmail, 
    formData.customerPhone, 
    formData.customerAddress, 
    formData.destination
  ])

  const handleSameAsCustomerToggle = () =>
    setFormData((prev) => ({
      ...prev,
      sameAsCustomer: !prev.sameAsCustomer
    }))

  // Load customer directory
  useEffect(() => {
    fetch(apiUrl("/api/customers/"))
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setCustomers(data)
        else if (Array.isArray(data?.customers)) setCustomers(data.customers)
        else setCustomers([])
      })
      .catch((e) => console.error("Failed to load customers:", e))
  }, [])

  // Load staff listing
  useEffect(() => {
    fetch(apiUrl("/api/employees/"))
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setEmployees(data)
        else setEmployees([])
      })
      .catch((e) => console.error("Failed to load employees:", e))
  }, [])

  // Load prefilled sales order details on Edit mode
  useEffect(() => {
    if (isEdit) {
      setInitialLoading(true)
      axios.get(apiUrl(`/api/sales/${id}`))
        .then((res) => {
          const data = res.data
          setFormData({
            customerId: data.customer_id,
            supplierId: data.supplier_id || 1,
            customerName: data.customer_name || "",
            customerEmail: data.customer_email || "",
            customerPhone: data.customer_phone || "",
            customerAddress: data.customer_address || "",
            sameAsCustomer: data.same_as_customer ?? false,
            billingName: data.billing_name || "",
            billingEmail: data.billing_email || "",
            billingPhone: data.billing_phone || "",
            billingAddress: data.billing_address || "",
            branch: data.branch || "Main Branch",
            salesperson: data.created_by || "",
            notes: data.notes || "",
            departureDate: data.departure_date ? data.departure_date.split("T")[0] : "",
            returnDate: data.return_date ? data.return_date.split("T")[0] : "",
            valueDate: data.value_date ? data.value_date.split("T")[0] : new Date().toISOString().split("T")[0],
            dueDate: data.due_date ? data.due_date.split("T")[0] : "",
            sellingPrice: data.selling_price || 0,
            purchaseCost: data.purchase_cost || 0,
            paidAmount: data.paid_amount || 0,
            discount: data.discount || 0,
            tax: data.tax || 0,
            destination: data.destination || "Dubai",
            items: data.items || [],
            documents: data.documents || [],
            currency: data.currency || "USD",
            exchangeRate: data.exchange_rate || 1.0,
            status: data.status || "draft",
            consolidatedInvoice: data.consolidated_invoice || false,
            consolidatedStatement: data.consolidated_statement || false,
            billingStreet: data.billing_street || "",
            billingCity: data.billing_city || "",
            billingZip: data.billing_zip || "",
            billingTelephone: data.billing_telephone || ""
          })
          if (data.customer_id) {
            setSelectedCustomer({
              id: data.customer_id,
              full_name: data.customer_name,
              email: data.customer_email,
              phone: data.customer_phone,
              address: data.customer_address
            })
          }
          setSavedSalesData(data)
          setSelectedSale({
            sales_id: data.id,
            invoice_id: data.invoice_id || data.invoice?.id || null
          })
          setSavedInvoiceId(data.invoice_id || data.invoice?.id || null)
        })
        .catch((e) => {
          console.error(e)
          alert("Error loading record details.")
        })
        .finally(() => setInitialLoading(false))
    }
  }, [id, isEdit])

  // Subtotal, taxes and balance calculations
  const calculatedSubtotal = useMemo(() => {
    return (formData.items || []).reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0)
  }, [formData.items])

  const calculatedGrandTotal = useMemo(() => {
    const sub = calculatedSubtotal
    const disc = Number(formData.discount || 0)
    const tx = Number(formData.tax || 0)
    return Math.max(sub - disc + tx, 0)
  }, [calculatedSubtotal, formData.discount, formData.tax])

  const calculatedBalance = useMemo(() => {
    return Math.max(calculatedGrandTotal - Number(formData.paidAmount || 0), 0)
  }, [calculatedGrandTotal, formData.paidAmount])

  // Customer checks on submit
  const CUSTOMER_LOOKUP_URLS = ["/api/customers/?email=", "/api/customers?email="]
  const CUSTOMER_CREATE_URLS = ["/api/customers/register", "/api/customers/register/"]

  const ensureCustomerId = async () => {
    if (selectedCustomer?.id) return selectedCustomer.id

    const name = (formData.customerName || "").trim()
    const email = (formData.customerEmail || "").trim()
    if (!name) throw new Error("Please select or enter customer account.")

    const localMatch = (customers || []).find((c) => {
      const e = (c?.email || c?.customer_email || "").toLowerCase()
      return e && e === email.toLowerCase()
    })
    if (localMatch?.id) {
      setSelectedCustomer(localMatch)
      return localMatch.id
    }

    for (const path of CUSTOMER_LOOKUP_URLS) {
      try {
        const { data } = await axios.get(apiUrl(`${path}${encodeURIComponent(email)}`))
        const found = (Array.isArray(data) ? data[0] : null) || data?.customer || (data?.id ? data : null)
        if (found?.id) {
          setSelectedCustomer(found)
          return found.id
        }
      } catch (e) {
        continue
      }
    }

    const payload = {
      branch_name: formData.branch || "Main Branch",
      relationship_staff: formData.salesperson || "System",
      customer_name: name,
      phone_number: formData.customerPhone || "",
      nationality: "Unknown",
      residence_country: "Unknown",
      full_address: formData.customerAddress || "",
      email: email,
      passport_number: null,
      trn_number: null,
      opening_balance: 0.0,
    }

    for (const path of CUSTOMER_CREATE_URLS) {
      try {
        const { data } = await axios.post(apiUrl(path), payload)
        const newId = data?.id ?? data?.customer?.id ?? data?.customer_id
        if (newId) return newId
      } catch (e) {
        continue
      }
    }

    throw new Error("Unable to create a new customer record. Please select one from the Directory list.")
  }

  // Validation — everything required before saving
  const canSave = !!formData.customerName && formData.items.length > 0

  const getMissingFields = () => {
    const missing = []
    if (!formData.customerName) missing.push("Customer account")
    if (!formData.items || formData.items.length === 0) missing.push("At least one item / service")
    return missing
  }

  // Pre-save Confirmation workflow trigger (Image 5)
  const handlePreSaveClick = (e, shouldClose = false) => {
    e?.preventDefault?.()
    const missing = getMissingFields()
    if (missing.length > 0) {
      alert(`⚠️ Cannot save sale. Please complete:\n• ${missing.join('\n• ')}`)
      return
    }
    setPendingSaveParams({ shouldClose })
    setShowConfirmSaveModal(true)
  }

  // Actual Save/POST API handler after confirmation
  const executeSave = async () => {
    if (!formData.items || formData.items.length === 0) {
      alert("⚠️ Cannot save sale: Please add at least one item or service.")
      return
    }
    setShowConfirmSaveModal(false)
    const { shouldClose } = pendingSaveParams || { shouldClose: false }
    setLoading(true)

    try {
      const nItems = normalizeItems(formData.items)
      const customerId = await ensureCustomerId()

      const calculatedPurchaseCost = (formData.items || []).reduce(
        (sum, item) => sum + ((Number(item.cost_price) || 0) * (Number(item.quantity) || 1)),
        0
      )

      const payload = {
        customer_id: customerId,
        customer_name: formData.customerName || "",
        customer_email: formData.customerEmail || "",
        customer_phone: formData.customerPhone || "",
        customer_address: formData.customerAddress || "",
        billing_full_name: formData.billingName || "",
        billing_email: formData.billingEmail || "",
        billing_mobile: formData.billingPhone || "",
        billing_address: formData.billingAddress || "",
        sales_date: new Date().toISOString(),
        value_date: formData.valueDate || null,
        due_date: formData.dueDate || null,
        departure_date: formData.departureDate || null,
        return_date: formData.returnDate || null,
        purchase_cost: calculatedPurchaseCost || 0,
        selling_price: calculatedSubtotal || 0,
        total_amount: calculatedSubtotal || 0,
        paid_amount: Number(formData.paidAmount || 0),
        balance_amount: calculatedBalance,
        discount: Number.parseFloat(formData.discount) || 0,
        tax: Number.parseFloat(formData.tax) || 0,
        grand_total: calculatedGrandTotal || 0,
        exchange_rate: Number.parseFloat(formData.exchangeRate) || 1.0,
        supplier_id: formData.supplierId ?? null,
        consolidated_invoice: !!formData.consolidatedInvoice,
        consolidated_statement: !!formData.consolidatedStatement,
        same_as_customer: !!formData.sameAsCustomer,
        items: nItems,
        status: formData.status || "draft",
        created_by: formData.salesperson || "system",
        billing_street: formData.billingStreet || "",
        billing_city: formData.billingCity || "",
        billing_zip: formData.billingZip || "",
        billing_telephone: formData.billingTelephone || ""
      }

      let res
      if (isEdit) {
        res = await axios.put(apiUrl(`/api/sales/${id}`), payload)
      } else {
        res = await axios.post(apiUrl("/api/sales/"), payload)
      }

      const salesId = res.data?.id || res.data?.sales_id
      let finalSalesData = { ...res.data }

      // Auto-generate invoice
      if (!finalSalesData.invoice?.id && !finalSalesData.invoice_id) {
        try {
          const invRes = await axios.post(apiUrl(`/api/invoices/generate/${salesId}`))
          finalSalesData.invoice = invRes.data
          finalSalesData.invoice_id = invRes.data.id
        } catch (e) {
          console.error("Auto generate invoice failed:", e)
        }
      }

      setSavedSalesData(finalSalesData)
      setSelectedSale({
        sales_id: salesId,
        invoice_id: finalSalesData.invoice?.id || finalSalesData.invoice_id || null,
      })
      setSavedInvoiceId(finalSalesData.invoice?.id || finalSalesData.invoice_id || salesId)

      alert(isEdit ? "Sale updated successfully! ✅" : "Sale order saved successfully! ✅")

      if (shouldClose) {
        navigate("/dashboard/sales/list")
      }
    } catch (err) {
      console.error(err)
      alert("Error saving sale. Check console logs.")
    } finally {
      setLoading(false)
    }
  }

  // Payments receipt vouchers
  const handleSaveReceipt = async () => {
    try {
      if (!selectedSale || !currentInvoiceId) return alert("Save sale first.")
      const payload = {
        sales_id: selectedSale.sales_id,
        invoice_id: currentInvoiceId,
        amount_paid: Number(amountPaid || 0),
        balance_remaining: remainingBalance,
        payment_mode: paymentMode,
        remarks: "",
      }
      await axios.post(apiUrl("/api/receipts/"), payload)
      alert("Receipt saved!")
      setShowReceiptModal(false)
    } catch (err) {
      console.error(err)
      alert("Receipt save error.")
    }
  }

  const handleGeneratePaymentVoucher = async () => {
    if (!savedSalesData?.id) return alert("Save sale first.")
    const customerName = savedSalesData.customer_name || "Client"
    const voucherPayload = {
      sales_id: savedSalesData.id,
      amount: savedSalesData.total_amount || calculatedGrandTotal,
      payment_method: paymentMode,
      payer: customerName,
      payee: "Rekonic",
      description: `Payment for Sale Order #${savedSalesData.id}`,
    }
    try {
      await axios.post(apiUrl("/api/payment_vouchers/"), voucherPayload)
      alert(`Payment Voucher saved for ${customerName}`)
      setShowVoucherModal(false)
    } catch (err) {
      console.error(err)
      alert("Failed to save payment voucher.")
    }
  }

  // Load summaries for modals
  useEffect(() => {
    if (showReceiptModal && currentInvoiceId) {
      axios.get(apiUrl(`/api/receipts/invoice/${currentInvoiceId}/summary`))
        .then((r) => setInvoiceSummary(r.data))
        .catch(() => setInvoiceSummary({
          invoice_id: currentInvoiceId,
          invoice_total: calculatedGrandTotal,
          total_paid: 0,
          remaining: calculatedBalance,
          status: "Outstanding",
        }))
    }
  }, [showReceiptModal, currentInvoiceId, calculatedGrandTotal, calculatedBalance])

  const remainingBalance = useMemo(() => {
    const paid = Number(amountPaid || 0)
    return Math.max(calculatedGrandTotal - paid, 0)
  }, [amountPaid, calculatedGrandTotal])

  // Customer searches
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery) return customers
    const q = customerSearchQuery.toLowerCase()
    return customers.filter(c => 
      (c.full_name && c.full_name.toLowerCase().includes(q)) ||
      (c.customer_name && c.customer_name.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      (c.phone && c.phone.includes(q)) ||
      (c.phone_number && c.phone_number.includes(q))
    )
  }, [customers, customerSearchQuery])

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.full_name || customer.customer_name || prev.customerName,
      customerEmail: customer.email || customer.customer_email || prev.customerEmail,
      customerPhone: customer.phone || customer.phone_number || customer.customer_phone || prev.customerPhone,
      customerAddress: customer.address || customer.customer_address || prev.customerAddress,
    }))
    setShowCustomerModal(false)
  }

  const handleCancelClick = () => {
    setShowDiscardModal(true)
  }

  if (initialLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted text-sm font-semibold">Loading sale order details...</p>
      </div>
    )
  }

  return (
    <div className="erp-sale-form-container px-4 py-3 bg-[#f8fafc]">
      {/* Sticky top action row bar */}
      <div className="erp-sticky-header bg-white border border-[#cbd5e1] shadow-sm rounded-3 p-3 mb-4 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <Button variant="link" className="p-0 text-muted" onClick={handleCancelClick}>
            <FaChevronLeft size={20} />
          </Button>
          <div>
            <h1 className="h4 text-[#0f2d59] font-bold mb-0">
              {isEdit ? `Bulk Sales - Edit #${id}` : "Bulk Sales - New"}
            </h1>
            <p className="text-xs text-muted mb-0">SALE FORM SHEET</p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* More Actions Left-expanding dropdown */}
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-flex align-items-center gap-2 border-[#cbd5e1]">
              <FaCog /> More Actions <FaChevronDown size={10} />
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow-lg border-[#cbd5e1] font-medium text-sm">
              {/* Receipt & Voucher only available AFTER sale is saved */}
              <Dropdown.Item
                disabled={!savedSalesData}
                title={!savedSalesData ? "Save the sale first to access receipt" : ""}
                onClick={() => savedSalesData && setShowReceiptModal(true)}
              >
                <FaFileInvoiceDollar className="me-2 text-primary" /> Invoice Receipt
                {!savedSalesData && <span className="ms-1 text-xs text-muted">(save first)</span>}
              </Dropdown.Item>
              <Dropdown.Item
                disabled={!savedSalesData}
                title={!savedSalesData ? "Save the sale first to generate voucher" : ""}
                onClick={() => {
                  if (!savedSalesData) return
                  setVoucherAmount(savedSalesData.total_amount || calculatedGrandTotal)
                  setVoucherPayer(formData.customerName)
                  setVoucherDescription(`Payment Voucher for Sale #${savedSalesData.id}`)
                  setShowVoucherModal(true)
                }}
              >
                <FaFileInvoiceDollar className="me-2 text-success" /> Payment Voucher
                {!savedSalesData && <span className="ms-1 text-xs text-muted">(save first)</span>}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Header>Pre-fill Services</Dropdown.Header>
              <Dropdown.Item onClick={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  if (!selectedCustomer?.id) { alert('Please select a customer before adding services.'); return; }
                  document.querySelector('.item-management')?.scrollIntoView({ behavior: 'smooth' })
                }}>
                  ✈️ Air Ticketing
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  if (!selectedCustomer?.id) { alert('Please select a customer before adding services.'); return; }
                  document.querySelector('.item-management')?.scrollIntoView({ behavior: 'smooth' })
                }}>
                  🏨 Hotel Booking
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => {
                  e.stopPropagation(); e.preventDefault();
                  if (!selectedCustomer?.id) { alert('Please select a customer before adding services.'); return; }
                  document.querySelector('.item-management')?.scrollIntoView({ behavior: 'smooth' })
                }}>
                  🎫 Visa Message
                </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="outline-secondary" size="sm" onClick={handleCancelClick} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={(e) => handlePreSaveClick(e, false)}
            disabled={loading || !canSave}
            title={!canSave ? getMissingFields().map(f => `Missing: ${f}`).join(' | ') : 'Save as draft'}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            className="btn-primary"
            size="sm"
            onClick={(e) => handlePreSaveClick(e, true)}
            disabled={loading || !canSave}
            title={!canSave ? getMissingFields().map(f => `Missing: ${f}`).join(' | ') : 'Save and go back to list'}
          >
            {loading ? "Processing..." : "Save & Close"}
          </Button>
        </div>
      </div>


      <Form onSubmit={(e) => e.preventDefault()}>
        <Row className="g-4">
          {/* Left Panel: Sale Specifications (Images 3 & 4 layout) */}
          <Col lg={6}>
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-header border-bottom border-slate-100 bg-white py-3 px-4">
                <h6 className="font-bold text-[#0f2d59] mb-0 tracking-tight">SALE SPECIFICATIONS</h6>
              </div>
              <div className="card-body p-4">
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Branch</Form.Label>
                      <Form.Select 
                        value={formData.branch}
                        onChange={(e) => handleInputChange("branch", e.target.value)}
                      >
                        <option value="Main Branch">Main Branch</option>
                        <option value="Downtown Branch">Downtown Branch</option>
                        <option value="Airport Branch">Airport Branch</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Value Date (Sales Date)</Form.Label>
                      <Form.Control 
                        type="date"
                        value={formData.valueDate}
                        onChange={(e) => handleInputChange("valueDate", e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Due Date</Form.Label>
                      <Form.Control 
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange("dueDate", e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Customer Account (RefId /Name /Mobile /Email)</Form.Label>
                      <InputGroup size="sm">
                        <Form.Control 
                          type="text"
                          required
                          placeholder="Select customer profile"
                          value={formData.customerName}
                          onClick={() => setShowCustomerModal(true)}
                          readOnly
                        />
                        <Button 
                          type="button" 
                          variant="outline-secondary"
                          onClick={() => setShowCustomerModal(true)}
                        >
                          <FaSearch />
                        </Button>
                      </InputGroup>
                      <div className="d-flex justify-content-between mt-1 text-xxs font-bold text-emerald-700">
                        <span>Credit Limit: No Limit</span>
                        <span>Account Balance: 0.00</span>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Staff Ref (Created By)</Form.Label>
                      <Form.Select 
                        value={formData.salesperson}
                        onChange={(e) => handleInputChange("salesperson", e.target.value)}
                      >
                        <option value="">Choose Staff</option>
                        {employees.map((emp) => {
                          const name = (emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`).trim() || `Staff #${emp.id}`;
                          return (
                            <option key={emp.id} value={name}>{name}</option>
                          )
                        })}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Exchange Rate (vs USD)</Form.Label>
                      <Form.Control 
                        type="number"
                        step="0.001"
                        value={formData.exchangeRate}
                        onChange={(e) => handleInputChange("exchangeRate", e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Check 
                      type="checkbox"
                      id="consolidated-inv-ch"
                      label="Consolidated Invoice"
                      className="text-xs font-semibold text-gray-700"
                      checked={formData.consolidatedInvoice}
                      onChange={(e) => handleInputChange("consolidatedInvoice", e.target.checked)}
                    />
                  </Col>

                  <Col sm={6}>
                    <Form.Check 
                      type="checkbox"
                      id="consolidated-stat-ch"
                      label="Consolidated Statement"
                      className="text-xs font-semibold text-gray-700"
                      checked={formData.consolidatedStatement}
                      onChange={(e) => handleInputChange("consolidatedStatement", e.target.checked)}
                    />
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Internal Notes</Form.Label>
                      <Form.Control 
                        as="textarea"
                        rows={2}
                        placeholder="Internal Notes"
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>

          {/* Right Panel: Billing details (Images 3 & 4 layout) */}
          <Col lg={6}>
            <div className="card shadow-sm border-0 h-100 bg-white">
              <div className="card-header border-bottom border-slate-100 bg-white py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="font-bold text-[#0f2d59] mb-0 tracking-tight">BILLING DETAILS</h6>
                <Form.Check 
                  type="checkbox"
                  id="same-customer-check"
                  label="SAME AS CUSTOMER"
                  className="text-[#0f2d59] text-xs font-bold mb-0"
                  checked={formData.sameAsCustomer}
                  onChange={handleSameAsCustomerToggle}
                />
              </div>
              <div className="card-body p-4">
                <Row className="g-3">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Full Name *</Form.Label>
                      <Form.Control 
                        type="text"
                        required
                        placeholder="Full Name"
                        value={formData.billingName}
                        onChange={(e) => handleInputChange("billingName", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Address *</Form.Label>
                      <Form.Control 
                        type="text"
                        required
                        placeholder="Address"
                        value={formData.billingAddress}
                        onChange={(e) => handleInputChange("billingAddress", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Street Name</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="Street Name"
                        value={formData.billingStreet}
                        onChange={(e) => handleInputChange("billingStreet", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">City</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="City"
                        value={formData.billingCity}
                        onChange={(e) => handleInputChange("billingCity", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">ZIP</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="ZIP/PIN"
                        value={formData.billingZip}
                        onChange={(e) => handleInputChange("billingZip", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Mobile</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="Mobile with country code"
                        value={formData.billingPhone}
                        onChange={(e) => handleInputChange("billingPhone", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Telephone</Form.Label>
                      <Form.Control 
                        type="text"
                        placeholder="Telephone with country code"
                        value={formData.billingTelephone}
                        onChange={(e) => handleInputChange("billingTelephone", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="text-xs font-bold text-gray-700">Email</Form.Label>
                      <Form.Control 
                        type="email"
                        placeholder="Enter valid email address"
                        value={formData.billingEmail}
                        onChange={(e) => handleInputChange("billingEmail", e.target.value)}
                        disabled={formData.sameAsCustomer}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </Form>

      {/* ⛔ Items & Documents are OUTSIDE the Form to prevent submit event bubbling */}
      <Row className="g-4 mt-0">
        {/* Full Width Row: Items and Services list */}
        <Col xs={12}>
          <div className="card shadow-sm border-0 bg-white">
            <div className="card-body p-4">
              <ItemManagement 
                items={formData.items} 
                onItemsChange={(updated) => handleInputChange("items", updated)} 
              />
            </div>
          </div>
        </Col>

        {/* Full Width Row: Document Upload */}
        <Col xs={12}>
          <div className="card shadow-sm border-0 bg-white">
            <div className="card-body p-4">
              <DocumentUpload
                apiBase={API_BASE}
                documents={formData.documents}
                onDocumentsChange={(updatedDocs) => handleInputChange("documents", updatedDocs)}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Calculations / Summary - now rendered at the very bottom (last) */}
      <Row className="g-4 mt-2">
        <Col md={6}></Col>
        <Col md={6}>
          <div className="card shadow-sm border-0 p-4 bg-white">
            <h6 className="font-bold text-[#0f2d59] border-bottom pb-2 mb-3">Grand Summary</h6>
            <div className="d-flex flex-column gap-2 text-sm">
              <div className="d-flex justify-content-between py-1 border-bottom">
                <span className="text-muted font-medium">Subtotal Amount:</span>
                <span className="font-bold text-gray-800">
                  {formData.currency} {calculatedSubtotal.toFixed(2)}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="text-muted font-medium">Global Discount:</span>
                <div style={{ maxWidth: "150px" }}>
                  <InputGroup size="sm">
                    <InputGroup.Text>{formData.currency}</InputGroup.Text>
                    <Form.Control 
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => handleInputChange("discount", e.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                <span className="text-muted font-medium">Global Tax:</span>
                <div style={{ maxWidth: "150px" }}>
                  <InputGroup size="sm">
                    <InputGroup.Text>{formData.currency}</InputGroup.Text>
                    <Form.Control 
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.tax}
                      onChange={(e) => handleInputChange("tax", e.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="d-flex justify-content-between py-1 border-bottom font-bold text-[#0f2d59]">
                <span>Grand Total ({formData.currency}):</span>
                <span>
                  {formData.currency} {calculatedGrandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Invoice Generator Display Section (loaded after Save) */}
      {savedSalesData && (
        <div className="mt-4 animate-in slide-in-from-bottom-4 duration-500 card shadow-sm border-0 p-4">
          <h4 className="mb-4 text-[#0077cc] font-bold flex items-center gap-2">
            <FaFileInvoiceDollar /> Generated Commercial Invoice
          </h4>
          <InvoiceGenerator salesData={savedSalesData} />
        </div>
      )}

      {/* Customer Lookup Search Modal */}
      <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} size="md" centered>
        <Modal.Header closeButton className="bg-[#f0f5fc]">
          <Modal.Title className="font-bold text-[#0f2d59]">Directory Search</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <InputGroup className="mb-3">
            <InputGroup.Text className="bg-white"><FaSearch /></InputGroup.Text>
            <Form.Control 
              type="text" 
              placeholder="Search customer by name, email, phone..." 
              value={customerSearchQuery}
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
            />
          </InputGroup>

          <div className="overflow-auto list-group rounded border" style={{ maxHeight: "300px" }}>
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-4 text-muted text-xs font-semibold">No customers registered yet.</div>
            ) : (
              filteredCustomers.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelectCustomer(c)}
                  className="list-group-item list-group-item-action d-flex flex-column align-items-start border-0 border-bottom text-start py-2.5 px-3"
                >
                  <div className="d-flex w-100 justify-content-between align-items-center">
                    <span className="font-bold text-[#0f2d59]">{c.full_name || c.customer_name}</span>
                    <span className="text-[10px] bg-slate-100 text-slate-600 font-semibold px-2 py-0.5 rounded">ID: #{c.id}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{c.email || "no-email@domain.com"} • {c.phone || c.phone_number || "No contact"}</div>
                </button>
              ))
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomerModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Discard Changes Warning Dialog */}
      <Modal show={showDiscardModal} onHide={() => setShowDiscardModal(false)} size="sm" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h5 text-warning font-bold">Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-3">
          <p className="text-sm text-gray-600 mb-0">
            Are you sure you want to go back? All unsaved modifications on this billing sheet will be lost.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowDiscardModal(false)}>Keep Editing</Button>
          <Button variant="danger" size="sm" onClick={() => navigate("/dashboard/sales/list")}>Discard & Leave</Button>
        </Modal.Footer>
      </Modal>

      {/* Save Confirmation Dialog */}
      <Modal show={showConfirmSaveModal} onHide={() => setShowConfirmSaveModal(false)} size="md" centered className="confirm-details-modal">
        <Modal.Header className="bg-[#0e3c74] text-white py-2 px-3 border-0">
          <Modal.Title className="h6 font-bold mb-0">⚠️ CONFIRM &amp; SAVE SALE ORDER</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white text-sm text-gray-700 font-semibold">
          <div className="mb-3 p-3 rounded" style={{ background: "#eef4ff", border: "1px solid #bfdbfe" }}>
            <div className="mb-2">Branch: <span className="text-[#0f2d59]">{formData.branch}</span></div>
            <div className="mb-2">Customer: <span className="text-[#0f2d59]">CS-B1-{formData.customerId || "NEW"}: {formData.customerName}</span></div>
            <div className="mb-2">Value Date: <span className="text-[#0f2d59]">{formatDate(formData.valueDate)}</span></div>
            <div className="mb-1">Items / Services: <span style={{ color: formData.items.length === 0 ? "#c2410c" : "#15803d", fontWeight: 700 }}>{formData.items.length} item(s)</span></div>
            <div>Grand Total: <span className="text-[#0f2d59]" style={{ fontWeight: 700 }}>{formData.currency} {calculatedGrandTotal.toFixed(2)}</span></div>
          </div>
          {formData.items.length === 0 && (
            <div className="mb-2 p-2 rounded text-xs" style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412" }}>
              ⚠️ <strong>No services/items have been added.</strong> Click <strong>CANCEL</strong> to go back and add services &amp; costs before saving.
            </div>
          )}
          <p className="text-xs text-gray-500 mb-0">Clicking <strong>CONFIRM &amp; SAVE</strong> will permanently record this sale order in the system.</p>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-[#f8fbfe] py-2 px-3">
          <Button variant="secondary" size="sm" onClick={() => setShowConfirmSaveModal(false)}>CANCEL (Keep Editing)</Button>
          <Button 
            className="btn-primary" 
            size="sm" 
            onClick={executeSave}
            disabled={formData.items.length === 0}
          >
            CONFIRM &amp; SAVE
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice Receipt Modal */}
      {showReceiptModal && (
        <div className="receipt-modal fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-md bg-white p-4 shadow">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-lg font-semibold">New Invoice Receipt</h4>
              <button onClick={() => setShowReceiptModal(false)} className="text-xl">&times;</button>
            </div>

            <div className="grid gap-3">
              <div className="grid gap-1">
                <Label>Payment Mode</Label>
                <select
                  className="rounded border p-2"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Credit Card">Credit Card</option>
                </select>
              </div>

              <div className="grid gap-1">
                <Label>Amount Paid</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="grid gap-1">
                <Label>Remaining Balance</Label>
                <Input readOnly value={remainingBalance.toFixed(2)} />
                {currentInvoiceId && (
                  <span className="text-xs text-gray-500">
                    Invoice #{currentInvoiceId}
                    {invoiceSummary?.status ? ` • ${invoiceSummary.status}` : ""}
                  </span>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowReceiptModal(false)}>Cancel</Button>
                <Button onClick={handleSaveReceipt}>Save Receipt</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Voucher Modal */}
      <Modal show={showVoucherModal} onHide={() => setShowVoucherModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Generate Payment Voucher</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Payment Method</label>
            <Form.Select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
            >
              <option value="Cash">Cash</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cheque">Cheque</option>
              <option value="Credit Card">Credit Card</option>
            </Form.Select>
          </div>

          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Payment Date</label>
            <Form.Control
              type="datetime-local"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>

          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Payer</label>
            <Form.Control type="text" value={voucherPayer} readOnly />
          </div>

          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Payee</label>
            <Form.Control type="text" value={voucherPayee} readOnly />
          </div>

          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Voucher Amount</label>
            <Form.Control type="number" value={voucherAmount} readOnly />
          </div>

          <div className="mb-3">
            <label className="text-xs font-bold text-gray-700 mb-1">Description</label>
            <Form.Control as="textarea" rows={2} value={voucherDescription} readOnly />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVoucherModal(false)}>Cancel</Button>
          <Button className="btn-primary" onClick={handleGeneratePaymentVoucher}>Generate Voucher</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )

  function formatDate(dateString) {
    if (!dateString) return "—";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }
}
